import {
  CRM_FIELD_TYPE,
  CrmField,
  CrmFieldValue,
  CrmFieldValueInput,
  CrmSection,
} from "types/Models/crmFields";

export const crmFieldFormKey = (field: CrmField): string =>
  field.instance_id ? `${field.instance_id}:${field.uuid}` : field.uuid;

/** Formik needs a concrete value per input type, where the API models "no answer" as null. */
export const toFormValue = (field: CrmField): CrmFieldValue => {
  const { value } = field;

  if (field.field_type === CRM_FIELD_TYPE.CHECKBOX) {
    return Boolean(value);
  }
  if (field.field_type === CRM_FIELD_TYPE.MULTI_SELECT) {
    return (value as string[]) || [];
  }
  if (value === null || value === undefined) {
    return "";
  }
  return value;
};

const sectionFields = (section: CrmSection): CrmField[] =>
  section.is_repeatable
    ? (section.instances || []).flatMap((instance) => instance.fields)
    : section.fields;

export const buildInitialValues = (sections: CrmSection[]) =>
  Object.fromEntries(
    sections.flatMap((section) =>
      sectionFields(section).map((field) => [
        crmFieldFormKey(field),
        toFormValue(field),
      ])
    )
  );

export const getEditableFields = (sections: CrmSection[]) =>
  sections.flatMap((section) =>
    sectionFields(section).filter((field) => !field.is_read_only)
  );

const isSameValue = (left: CrmFieldValue, right: CrmFieldValue) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

/** Only fields the user actually touched are sent, so one save never overwrites the rest. */
export const buildChangedValues = (
  sections: CrmSection[],
  formValues: Record<string, CrmFieldValue>
): CrmFieldValueInput[] =>
  getEditableFields(sections)
    .filter((field) => {
      const key = crmFieldFormKey(field);
      // A key absent from formValues (e.g. a section instance added after the form last
      // synced) was never loaded into form state, let alone edited - not a change.
      if (!(key in formValues)) return false;
      return !isSameValue(toFormValue(field), formValues[key]);
    })
    .map((field) => ({
      field_id: field.uuid,
      ...(field.instance_id ? { instance_id: field.instance_id } : {}),
      value: normaliseForApi(formValues[crmFieldFormKey(field)]),
    }));

const normaliseForApi = (value: CrmFieldValue): CrmFieldValue => {
  if (value === "" || value === undefined) {
    return null;
  }
  if (Array.isArray(value) && value.length === 0) {
    return null;
  }
  return value;
};
